import { FunctionComponent, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import { BulkAddStepProps } from "../BulkAddDialogTypes";
import { getHref } from "../../../utils/getHref";
import LinkIcon from "@mui/icons-material/Link";
import { api } from "../../api";
import { useApiMutation } from "../../hooks/useApiMutation";

export const BackupDataStep: FunctionComponent<BulkAddStepProps> = ({
  onNext,
  organizationId,
  orgSubdomain,
}) => {
  const [exportStatus, setExportStatus] = useState<
    null | "success" | "error" | "processing"
  >(null);
  const [exportInitiated, setExportInitiated] = useState(false);
  const handleGoToEnhancedAssets = () => {
    window.location.assign(`${getHref(orgSubdomain)}/enhanced_assets`);
  };

  const { trigger: createExportJob } = useApiMutation(api.myExports.create);

  const handleExportRequest = async () => {
    setExportStatus(null);
    try {
      await createExportJob({
        args: [
          {
            myExportsCreateRequest: {
              target_entity: "organization",
              target_id: organizationId,
              export_entity: "assets",
              columns: [],
              filters: "",
            },
          },
        ],
      });
      setExportInitiated(true);
      setExportStatus("success");
    } catch (e) {
      setExportStatus("error");
    }
  };

  return (
    <Box css={(theme) => ({ padding: theme.spacing(2) })}>
      <Typography fontWeight="bold" gutterBottom>
        Step 1: Back up your data
      </Typography>
      <Typography gutterBottom>
        Click here to create an export of your assets before proceeding. You can
        use this to revert changes if needed.
      </Typography>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={2}
      >
        <Box display="flex" alignItems="center" gap={1}>
          {organizationId && (
            <>
              <Button
                color="primary"
                variant="contained"
                onClick={handleExportRequest}
                disabled={exportInitiated}
              >
                Back Up All Assets
              </Button>
              {exportInitiated && exportStatus !== "error" && (
                <Typography color="success" variant="body2" sx={{ ml: 1 }}>
                  Your asset export has been initiated. You can check its status
                  in the export list.
                </Typography>
              )}
              {exportStatus === "error" && (
                <Box display="flex" alignItems="center" gap={0.5}>
                  <CancelIcon color="error" data-testid="export-error" />
                  <Typography color="error" variant="body2">
                    Something went wrong
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Box>
        <Box display="flex" alignItems="center" gap={2}>
          {orgSubdomain && (
            <Button
              color="primary"
              variant="contained"
              startIcon={<LinkIcon />}
              onClick={handleGoToEnhancedAssets}
            >
              Go to Enhanced Assets
            </Button>
          )}
          <Button
            variant="outlined"
            onClick={onNext}
            disabled={exportStatus === "processing"}
          >
            Continue
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
